import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import FollowButton from '../../components/social/FollowButton';
import {
  PublicProfile,
  SocialSummary,
  getPublicProfile,
  getSocialSummary,
} from '../../../services/social/api';
import { getUserPosts, ServerPost } from '../../../services/posts/api';
import './PublicProfilePage.css';

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
    <stop stop-color="#0d4f3c"/><stop offset="1" stop-color="#d9ff31"/>
  </linearGradient></defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>`)}`;

const PublicProfilePage: React.FC = () => {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = React.useState<PublicProfile | null>(null);
  const [social, setSocial] = React.useState<SocialSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [posts, setPosts] = React.useState<ServerPost[]>([]);
  const [postsLocked, setPostsLocked] = React.useState(false);
  const [postsLoading, setPostsLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getPublicProfile(userId), getSocialSummary(userId)])
      .then(([publicProfile, summary]) => {
        if (cancelled) return;
        setProfile(publicProfile);
        setSocial(summary);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not load this profile.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Viewing your own public page → send to the editable profile.
  React.useEffect(() => {
    if (social?.isSelf) navigate('/profile', { replace: true });
  }, [social?.isSelf, navigate]);

  // Their posts (T2.10a) — follower-gated server-side (403 → locked notice).
  // Depends on isFollowing so tapping Follow/Unfollow re-resolves the gate.
  React.useEffect(() => {
    if (!userId || !social || social.isSelf) return undefined;
    let cancelled = false;
    setPostsLoading(true);
    getUserPosts(userId)
      .then((result) => {
        if (cancelled) return;
        setPostsLocked(Boolean(result.forbidden));
        setPosts(result.posts);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      })
      .finally(() => {
        if (!cancelled) setPostsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, social?.isFollowing, social?.isSelf]);

  const formatPostTime = (createdAt: string): string => {
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    profile?.nickname ||
    'Traveler';
  const handle = profile?.nickname ? `@${profile.nickname}` : '';

  const adjustFollowers = (following: boolean) =>
    setSocial((current) =>
      current ? { ...current, followers: Math.max(0, current.followers + (following ? 1 : -1)), isFollowing: following } : current,
    );

  return (
    <DashboardLayout activeSection="feed">
      <div className="v2-public-profile">
        {loading && <p className="v2-public-profile__state">Loading profile…</p>}

        {!loading && error && (
          <div className="v2-public-profile__state v2-public-profile__state--error">
            <p>{error}</p>
            <button type="button" onClick={() => navigate(-1)}>Go back</button>
          </div>
        )}

        {!loading && !error && !profile && (
          <div className="v2-public-profile__state v2-public-profile__state--error">
            <p>This user doesn’t exist yet.</p>
            <button type="button" onClick={() => navigate('/feed')}>Back to feed</button>
          </div>
        )}

        {!loading && !error && profile && (
          <section className="v2-public-profile__card">
            <img
              className="v2-public-profile__avatar"
              src={profile.pictureUrl || fallbackAvatar}
              alt=""
              onError={(event) => {
                event.currentTarget.src = fallbackAvatar;
              }}
            />
            <div className="v2-public-profile__identity">
              <h1>{displayName}</h1>
              {handle && <p className="v2-public-profile__handle">{handle}</p>}
              <div className="v2-public-profile__stats">
                <span><strong>{social?.followers ?? 0}</strong> Followers</span>
                <span><strong>{social?.following ?? 0}</strong> Following</span>
              </div>
            </div>
            {social && !social.isSelf && (
              <FollowButton
                userId={userId}
                initialFollowing={social.isFollowing}
                initialState={social.followState}
                onChange={adjustFollowers}
              />
            )}
          </section>
        )}

        {!loading && !error && profile && (
          <section className="v2-public-profile__posts" aria-labelledby="public-posts-heading">
            <h2 id="public-posts-heading">Posts</h2>
            {postsLoading && <p className="v2-public-profile__posts-notice">Loading posts…</p>}
            {!postsLoading && postsLocked && (
              <p className="v2-public-profile__posts-notice">
                Follow {displayName} to see their posts.
              </p>
            )}
            {!postsLoading && !postsLocked && posts.length === 0 && (
              <p className="v2-public-profile__posts-notice">No posts yet.</p>
            )}
            {!postsLoading && !postsLocked && posts.map((post) => (
              <article key={post.postId} className="v2-public-profile__post">
                <header>
                  <strong>{displayName}</strong>
                  <span>{formatPostTime(post.createdAt)}</span>
                </header>
                {(post.media || []).some((item) => item.status === 'live') && (
                  <div className="v2-public-profile__post-media">
                    {(post.media || [])
                      .filter((item) => item.status === 'live' && (item.thumbUrl || item.url))
                      .map((item) => (
                        <img key={item.mediaId} src={item.thumbUrl || item.url} alt="" />
                      ))}
                  </div>
                )}
                <p>{post.text}</p>
              </article>
            ))}
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PublicProfilePage;
